<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import LeaderBord from "@/components/leaderBord.vue";
import ActiveSpeakingRooms from "@/components/ActiveSpeakingRooms.vue";
import FriendshipBox from "@/components/FriendshipBox.vue";
import SocialInbox from "@/components/SocialInbox.vue";
import ProfileMenu from "@/components/ProfileMenu.vue";
import cocoMascot from "@/assets/coco-mascot.png";
import cocoLeitner from "@/assets/coco-leitner.png";
import { getMyStreak } from "@/api/streaks";
import { getLeitnerOverview } from "@/api/leitner";
import { handleError } from "@/services/errorHandler";
import {
  enablePush,
  notificationPermission,
  notificationsEnabled,
} from "@/composables/usePushNotifications";
import type { LeitnerOverview, Streak } from "@/types";

const props = defineProps({
  defaultSelected: {
    type: String,
    default: "mock",
  },
});

const emit = defineEmits(["select"]);
const router = useRouter();

const servicesBase = [
  {
    id: "mock",
    title: "آزمون شبیه‌ساز",
    subtitle: "آزمون کامل آیلتس در شرایط واقعی",
    meta: "۴ آزمون در دسترس",
    icon: "clipboard",
  },
  {
    id: "speaking",
    title: "اسپیکینگ",
    subtitle: "تمرین مکالمه با هوش مصنوعی",
    meta: "۳ بخش تمرینی",
    icon: "mic",
  },
  {
    id: "writing",
    title: "رایتینگ",
    subtitle: "بهبود مهارت نوشتن Task 1 & Task 2",
    meta: "بازخورد هوشمند",
    icon: "pen",
  },
  {
    id: "vocabulary",
    title: "جعبه لایتنر",
    subtitle: "مرور فاصله‌دار واژگان آیلتس",
    meta: "واژگان",
    icon: "book",
  },
  {
    id: "talk",
    title: "صحبت کنیم",
    subtitle: "نظر بده، پاسخ بگیر و لایک کن",
    meta: "جامعه یادگیری",
    icon: "chat",
  },
];

const streak = ref<Streak | null>(null);
const streakLoading = ref(false);
const streakError = ref<string | null>(null);
const leitner = ref<LeitnerOverview | null>(null);
const notifyBusy = ref(false);

const showNotifyPrompt = computed(
  () => notificationsEnabled() && notificationPermission.value === "default"
);

const showNotifyBlocked = computed(
  () => notificationsEnabled() && notificationPermission.value === "denied"
);

async function enableNotifications() {
  notifyBusy.value = true;
  try {
    await enablePush();
  } finally {
    notifyBusy.value = false;
  }
}

function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);
}

const services = computed(() =>
  servicesBase.map((service) => {
    if (service.id !== "vocabulary") return service;
    const due = leitner.value?.due_count ?? 0;
    return {
      ...service,
      meta:
        due > 0
          ? `${toPersianDigits(due)} کارت برای مرور`
          : `${toPersianDigits(leitner.value?.total_cards ?? 0)} کارت فعال`,
    };
  })
);

const todayProgress = computed(() => {
  const current = streak.value?.current_streak ?? 0;
  const goal = streak.value?.daily_goal ?? 1;
  const sessions = streak.value?.today.sessions_completed ?? 0;
  const minutes = streak.value?.today.practice_minutes ?? 0;
  const xp = streak.value?.today.xp_earned ?? 0;

  return [
    {
      id: "streak",
      label: "روز متوالی",
      value: toPersianDigits(current),
      icon: "flame",
      color: "#F59E0B",
    },
    {
      id: "goal",
      label: "هدف امروز",
      value: `${toPersianDigits(Math.min(sessions, goal))}/${toPersianDigits(goal)}`,
      icon: "target",
      color: "#7C4DFF",
    },
    {
      id: "time",
      label: "زمان تمرین",
      value: `${toPersianDigits(minutes)} دقیقه`,
      icon: "clock",
      color: "#79C26D",
    },
    {
      id: "xp",
      label: "امتیاز امروز",
      value: `${toPersianDigits(xp)} XP`,
      icon: "trophy",
      color: "#E8A317",
    },
  ];
});

const leitnerBoxes = computed(() => {
  if (leitner.value?.boxes?.length) return leitner.value.boxes;
  return [1, 2, 3, 4, 5].map((box) => ({ box, count: 0 }));
});

async function loadStreak() {
  streakLoading.value = true;
  streakError.value = null;
  try {
    streak.value = await getMyStreak();
  } catch (e) {
    streakError.value = handleError(e, "IELTS.loadStreak");
  } finally {
    streakLoading.value = false;
  }
}

async function loadLeitner() {
  try {
    leitner.value = await getLeitnerOverview();
  } catch (e) {
    handleError(e, "IELTS.loadLeitner");
  }
}

onMounted(() => {
  loadStreak();
  loadLeitner();
});

const active = ref(localStorage.getItem("selectedService") || props.defaultSelected);

function select(id) {
  active.value = id;
  localStorage.setItem("selectedService", id);
  emit("select", id);
}

function openService(id) {
  select(id);

  const routes = {
    mock: "/mock-exams",
    speaking: "/dashboard/speaking",
    writing: "/dashboard/writing",
    vocabulary: "/dashboard/leitner",
    talk: "/dashboard/talk",
  };

  if (routes[id]) {
    router.push(routes[id]);
  }
}

function openLeitner() {
  router.push("/dashboard/leitner");
}

function onKeydown(event, index) {
  const list = services.value;
  const last = list.length - 1;
  let nextIndex = null;

  if (event.key === "ArrowRight" || event.key === "ArrowDown") {
    nextIndex = index === last ? 0 : index + 1;
  } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
    nextIndex = index === 0 ? last : index - 1;
  }

  if (nextIndex !== null) {
    event.preventDefault();
    const id = list[nextIndex].id;
    select(id);
    const el = document.getElementById(`service-card-${id}`);
    el?.focus();
  } else if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    openService(list[index].id);
  }
}
</script>

<template>
  <section class="ielts-panel" aria-labelledby="ielts-panel-heading">
    <div class="ielts-panel__decor" aria-hidden="true">
      <svg class="leaf leaf--1" viewBox="0 0 48 64" fill="none">
        <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.55" />
        <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.5" />
      </svg>
      <svg class="leaf leaf--2" viewBox="0 0 48 64" fill="none">
        <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.45" />
        <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.4" />
      </svg>
      <svg class="leaf leaf--3" viewBox="0 0 48 64" fill="none">
        <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.5" />
        <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.45" />
      </svg>
      <svg class="leaf leaf--4" viewBox="0 0 48 64" fill="none">
        <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.4" />
        <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.35" />
      </svg>
      <svg class="leaf leaf--5" viewBox="0 0 48 64" fill="none">
        <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.4" />
        <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.35" />
      </svg>
    </div>

    <div class="container">
      <div class="ielts-panel__topbar">
        <button
          v-if="showNotifyPrompt"
          type="button"
          class="ielts-panel__notify"
          :disabled="notifyBusy"
          @click="enableNotifications"
        >
          <i class="bi bi-bell" aria-hidden="true" />
          فعال‌سازی اعلان‌ها
        </button>
        <p v-else-if="showNotifyBlocked" class="ielts-panel__notify-hint">
          اعلان‌های مرورگر مسدود است. از قفل کنار نوار آدرس، Notifications را Allow کنید.
        </p>
        <ProfileMenu />
      </div>

      <div class="ielts-panel__layout">
        <aside class="ielts-panel__social" aria-label="دوستان و پیام‌ها">
          <FriendshipBox />
          <SocialInbox />
        </aside>

        <div class="ielts-panel__main">
          <section class="today-progress" aria-label="پیشرفت امروز" :aria-busy="streakLoading">
            <h3 class="today-progress__title">پیشرفت امروز</h3>
            <p v-if="streakError" class="today-progress__error">{{ streakError }}</p>
            <div class="today-progress__stats">
              <div
                v-for="item in todayProgress"
                :key="item.id"
                class="today-progress__item"
                :class="{ 'today-progress__item--loading': streakLoading }"
              >
                <span
                  class="today-progress__icon"
                  :style="{ background: `${item.color}22`, color: item.color }"
                  aria-hidden="true"
                >
                  <svg v-if="item.icon === 'flame'" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12 2s4 4.2 4 8a4 4 0 1 1-8 0c0-2.4 1.4-4.6 2.6-6.2.4-.5 1.4.1 1.2.8C11.4 6.2 12 8 12 9c0-3-1.5-5.5-3-7 3 1 5 4 5 7a5 5 0 1 1-10 0c0-3.5 2.5-6.5 5-9z" />
                  </svg>
                  <svg v-else-if="item.icon === 'target'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="8" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="12" cy="12" r="1" fill="currentColor" />
                  </svg>
                  <svg v-else-if="item.icon === 'clock'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" stroke-linecap="round" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 14.8 7.2 17l.9-5.4L4.2 7.7l5.4-.8L12 2z" />
                  </svg>
                </span>
                <div class="today-progress__text">
                  <strong>{{ item.value }}</strong>
                  <span>{{ item.label }}</span>
                </div>
              </div>
            </div>
          </section>

          <div class="ielts-panel__intro">
            <span class="ielts-panel__eyebrow">مرکز تمرین</span>
            <h2 id="ielts-panel-heading" class="ielts-panel__title">
              امروز دوست داری چی تمرین کنیم؟
            </h2>
            <p class="ielts-panel__subtitle">
              بخش مورد نظر رو انتخاب کن تا تمرینات با سطح و پیشرفتت هماهنگ بشه.
            </p>
          </div>

          <div
            class="ielts-panel__cards"
            role="radiogroup"
            aria-labelledby="ielts-panel-heading"
          >
            <button
              v-for="(service, index) in services"
              :id="`service-card-${service.id}`"
              :key="service.id"
              type="button"
              role="radio"
              class="service-card"
              :class="{ 'service-card--active': active === service.id }"
              :aria-checked="active === service.id"
              :aria-label="`${service.title}: ${service.subtitle}`"
              @click="openService(service.id)"
              @keydown="(e) => onKeydown(e, index)"
            >
              <span
                v-if="active === service.id"
                class="service-card__beacon"
                aria-hidden="true"
              ></span>

              <span class="service-card__icon">
                <svg
                  v-if="service.icon === 'clipboard'"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  aria-hidden="true"
                >
                  <path d="M9 11l3 3L22 4" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else-if="service.icon === 'mic'"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  aria-hidden="true"
                >
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke-linecap="round" />
                  <line x1="12" y1="19" x2="12" y2="23" stroke-linecap="round" />
                  <line x1="8" y1="23" x2="16" y2="23" stroke-linecap="round" />
                </svg>
                <svg
                  v-else-if="service.icon === 'pen'"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  aria-hidden="true"
                >
                  <path d="M12 20h9" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else-if="service.icon === 'book'"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  aria-hidden="true"
                >
                  <path
                    d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  aria-hidden="true"
                >
                  <path
                    d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>

              <h3 class="service-card__title">{{ service.title }}</h3>
              <p class="service-card__subtitle">{{ service.subtitle }}</p>

              <div class="service-card__footer">
                <span class="service-card__meta">{{ service.meta }}</span>
                <svg
                  class="service-card__arrow"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path d="M7 17 17 7M7 7h10v10" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>

              <span class="service-card__bar" aria-hidden="true"></span>
            </button>
          </div>

          <div class="ielts-panel__bottom">
            <section
              class="leitner-summary"
              aria-label="جعبه لایتنر"
              @click="openLeitner"
              @keydown.enter="openLeitner"
              role="button"
              tabindex="0"
            >
              <div class="leitner-summary__head">
                <h3 class="leitner-summary__title">جعبه لایتنر</h3>
                <span class="leitner-summary__due">
                  {{
                    leitner
                      ? `${toPersianDigits(leitner.due_count)} آماده مرور`
                      : "—"
                  }}
                </span>
              </div>
              <div class="leitner-summary__body">
                <div class="leitner-summary__boxes">
                  <div
                    v-for="box in leitnerBoxes"
                    :key="box.box"
                    class="leitner-summary__box"
                    :class="`leitner-summary__box--${box.box}`"
                  >
                    <span>{{ toPersianDigits(box.box) }}</span>
                    <strong>{{ toPersianDigits(box.count) }}</strong>
                  </div>
                </div>
                <img
                  :src="cocoLeitner"
                  alt=""
                  class="leitner-summary__mascot"
                  aria-hidden="true"
                />
              </div>
            </section>

            <div class="ielts-panel__mascot-slot">
              <svg class="ielts-panel__mascot-leaf ielts-panel__mascot-leaf--left" viewBox="0 0 48 64" fill="none" aria-hidden="true">
                <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.5" />
                <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.45" />
              </svg>
              <svg class="ielts-panel__mascot-leaf ielts-panel__mascot-leaf--right" viewBox="0 0 48 64" fill="none" aria-hidden="true">
                <path d="M24 2C14 14 6 28 8 44c2 12 10 18 16 18s14-6 16-18C42 28 34 14 24 2Z" fill="#A5B94F" opacity="0.45" />
                <path d="M24 8v46" stroke="#8FA03E" stroke-width="1.5" stroke-linecap="round" opacity="0.4" />
              </svg>
              <img
                :src="cocoMascot"
                alt=""
                class="ielts-panel__mascot"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <aside class="ielts-panel__sidebar" aria-label="اتاق‌های اسپیکینگ و جدول امتیازات">
          <ActiveSpeakingRooms />
          <LeaderBord />
        </aside>
      </div>
    </div>
  </section>
</template>

<style scoped>
.ielts-panel {
  --color-primary: #6D5EF8;
  --color-secondary: #6A4328;
  --color-bg: #FCF9F6;
  --color-card: #FFFFFF;
  --color-border: #ECE4D9;
  --color-text: #4C372A;
  --color-muted: #8C8176;
  --color-success: #79C26D;
  --color-warning: #865FEA;

  position: fixed;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  direction: ltr;
  background: var(--color-bg);
  padding: 56px 0 80px;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
  box-sizing: border-box;
}

.ielts-panel__decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.leaf {
  position: absolute;
  width: 36px;
  height: 48px;
}

.leaf--1 { top: 8%; right: 42%; width: 42px; height: 56px; transform: rotate(25deg); }
.leaf--2 { top: 38%; left: 6%; width: 34px; height: 46px; transform: rotate(-40deg); }
.leaf--3 { top: 58%; left: 14%; width: 28px; height: 38px; transform: rotate(55deg); }
.leaf--4 { top: 72%; left: 4%; width: 40px; height: 52px; transform: rotate(-15deg); }
.leaf--5 { top: 18%; left: 28%; width: 30px; height: 40px; transform: rotate(70deg); }

.ielts-panel > .container {
  position: relative;
  z-index: 1;
  direction: rtl;
  max-width: 1480px;
  box-sizing: border-box;
  padding-inline: 16px;
}

.ielts-panel__topbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 28px;
}

.ielts-panel__notify {
  margin-inline-end: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 8px 16px;
  background: #6d5ef8;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.ielts-panel__notify:disabled {
  opacity: 0.65;
  cursor: wait;
}

.ielts-panel__notify-hint {
  margin: 0 auto 0 0;
  font-size: 12px;
  color: #8a7f74;
  max-width: 280px;
  line-height: 1.5;
}

.ielts-panel__layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 300px;
  gap: 32px;
  align-items: stretch;
  min-width: 0;
  max-width: 100%;
  direction: ltr;
}

.ielts-panel__main,
.ielts-panel__sidebar,
.ielts-panel__social {
  direction: rtl;
}

.ielts-panel__main {
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 22px;
  min-height: 100%;
}

.ielts-panel__sidebar,
.ielts-panel__social {
  width: 100%;
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ielts-panel__sidebar {
  margin-right: -24px;
}

.ielts-panel__social {
  margin-left: -24px;
}

.ielts-panel__intro {
  max-width: 560px;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.ielts-panel__eyebrow {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-secondary);
  margin-bottom: 8px;
}

.ielts-panel__title {
  font-size: 30px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 8px;
  line-height: 1.3;
}

.ielts-panel__subtitle {
  font-size: 14px;
  color: var(--color-muted);
  margin: 0;
  line-height: 1.6;
}

.ielts-panel__cards {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  justify-content: flex-start;
  margin-left: 28px;
}

.ielts-panel__bottom {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 18px;
  align-items: end;
  margin-top: auto;
  margin-bottom: 52px;
  margin-left: 28px;
  padding-top: 12px;
  direction: rtl;
}

.ielts-panel__mascot-slot {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  min-height: 140px;
  overflow: visible;
}

.ielts-panel__mascot-slot::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 72px;
  transform: translateX(-50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(124, 77, 255, 0.1) 0%, rgba(124, 77, 255, 0.03) 55%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.ielts-panel__mascot-leaf {
  position: absolute;
  width: 28px;
  height: 38px;
  z-index: 2;
  pointer-events: none;
}

.ielts-panel__mascot-leaf--left {
  left: 2px;
  top: -18px;
  transform: rotate(-38deg);
}

.ielts-panel__mascot-leaf--right {
  right: 4px;
  top: -28px;
  transform: rotate(28deg);
}

.ielts-panel__mascot {
  position: relative;
  z-index: 1;
  width: 180px;
  height: auto;
  max-width: 100%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  transform: translateY(-68px);
  filter: drop-shadow(0 4px 8px rgba(109, 94, 248, 0.08));
}

.leitner-summary {
  grid-column: 1;
  width: 100%;
  min-width: 0;
  background: #F7F2EA;
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 10px 12px 12px;
  direction: rtl;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  overflow: visible;
}

.leitner-summary:hover,
.leitner-summary:focus-visible {
  border-color: rgba(124, 77, 255, 0.45);
  box-shadow: 0 6px 16px rgba(109, 94, 248, 0.1);
  outline: none;
}

.leitner-summary__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.leitner-summary__title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text);
}

.leitner-summary__due {
  font-size: 11px;
  font-weight: 600;
  color: #7C4DFF;
}

.leitner-summary__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 88px;
  gap: 10px;
  align-items: end;
  direction: ltr;
}

.leitner-summary__boxes {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
  min-width: 0;
  direction: rtl;
}

.leitner-summary__box {
  background: #fff;
  border: 1px solid #E5DDD2;
  border-radius: 10px;
  padding: 6px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.leitner-summary__box span {
  font-size: 10px;
  color: var(--color-muted);
}

.leitner-summary__box strong {
  font-size: 13px;
  color: var(--color-text);
}

.leitner-summary__box--5 strong {
  color: #79C26D;
}

.leitner-summary__mascot {
  width: 88px;
  height: auto;
  justify-self: center;
  align-self: end;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  transform: translateY(4px);
  filter: drop-shadow(0 4px 8px rgba(76, 55, 42, 0.1));
}

.today-progress {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: #F7F2EA;
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 10px 8px 12px;
  direction: rtl;
  margin-left: 28px;
  box-sizing: border-box;
}

.today-progress__title {
  margin: 0 12px 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text);
}

.today-progress__error {
  margin: 0 12px 6px;
  font-size: 11px;
  color: #c45c5c;
}

.today-progress__item--loading {
  opacity: 0.55;
}

.today-progress__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
}

.today-progress__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 0;
  padding: 2px 6px;
  text-align: center;
  border-left: 1px solid #E5DDD2;
}

.today-progress__item:last-child {
  border-left: none;
}

.today-progress__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.today-progress__icon svg {
  width: 14px;
  height: 14px;
}

.today-progress__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  min-width: 0;
}

.today-progress__text strong {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.15;
  white-space: nowrap;
}

.today-progress__text span {
  font-size: 10px;
  color: var(--color-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.service-card {
  position: relative;
  flex: 1 1 calc(20% - 15px);
  min-width: 160px;
  min-height: 280px;
  text-align: right;
  background: #ffffff;
  border: 2px solid transparent;
  border-radius: 24px;
  padding: 24px 18px 20px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(76, 55, 42, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  overflow: visible;
}

.service-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 28px rgba(76, 55, 42, 0.08);
}

.service-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(124, 77, 255, 0.25);
}

.service-card:hover .service-card__icon {
  background: #EFEAFF;
}

.service-card:hover .service-card__arrow {
  color: #7C4DFF;
  transform: translate(-2px, -2px);
}

.service-card--active {
  background: #FBF7FC;
  border-color: #7C4DFF;
  box-shadow:
    0 12px 32px rgba(124, 77, 255, 0.16),
    0 4px 12px rgba(124, 77, 255, 0.08);
}

.service-card--active:hover {
  transform: translateY(-3px);
  box-shadow:
    0 14px 36px rgba(124, 77, 255, 0.2),
    0 6px 14px rgba(124, 77, 255, 0.1);
}

.service-card--active .service-card__icon {
  background: #F3EFFF;
  color: #7C4DFF;
}

.service-card--active .service-card__meta {
  color: #7C4DFF;
  font-weight: 600;
}

.service-card--active .service-card__arrow {
  color: #7C4DFF;
}

.service-card--active .service-card__bar {
  opacity: 1;
}

.service-card__beacon {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #7C4DFF;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(124, 77, 255, 0.35);
  z-index: 3;
}

.service-card__beacon::after {
  content: "";
  width: 8px;
  height: 4.5px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translateY(-0.5px);
  display: block;
}

.service-card__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #F3EFFF;
  color: #7C4DFF;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.25s ease;
  flex-shrink: 0;
}

.service-card__title {
  font-size: 18px;
  font-weight: 700;
  color: #4C372A;
  margin: 0;
  line-height: 1.35;
}

.service-card__subtitle {
  font-size: 13px;
  line-height: 1.65;
  color: #8C8176;
  margin: 0;
  flex: 1;
}

.service-card__footer {
  margin-top: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 6px;
}

.service-card__meta {
  font-size: 13px;
  font-weight: 500;
  color: #A89B90;
  text-align: right;
}

.service-card__arrow {
  color: #C9BDB2;
  flex-shrink: 0;
  transition: transform 0.25s ease, color 0.25s ease;
}

.service-card__bar {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 0;
  height: 3px;
  border-radius: 4px 4px 0 0;
  background: linear-gradient(90deg, #7C4DFF, #9B7CFF);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

@media (max-width: 1199.98px) {
  .ielts-panel__layout {
    grid-template-columns: 1fr;
  }

  .ielts-panel__title {
    font-size: 28px;
  }

  .ielts-panel__main {
    order: 1;
  }

  .ielts-panel__social,
  .ielts-panel__sidebar {
    width: 100%;
    max-width: 420px;
    position: static;
    margin-right: 0;
    margin-left: 0;
  }

  .ielts-panel__social {
    order: 2;
  }

  .ielts-panel__sidebar {
    order: 3;
  }

  .ielts-panel__cards {
    flex-wrap: wrap;
    margin-left: 0;
  }

  .today-progress {
    margin-left: 0;
  }

  .service-card {
    flex: 1 1 calc(50% - 9px);
    min-width: 180px;
  }

  .ielts-panel__mascot {
    width: 110px;
  }
}

@media (max-width: 767.98px) {
  .ielts-panel__bottom {
    grid-template-columns: 1fr;
    margin-left: 0;
  }

  .leitner-summary,
  .ielts-panel__mascot-slot {
    grid-column: 1;
  }

  .leitner-summary__body {
    grid-template-columns: minmax(0, 1fr) 72px;
  }

  .leitner-summary__mascot {
    width: 72px;
  }

  .ielts-panel__mascot-slot {
    grid-row: auto;
    order: -1;
    min-height: 120px;
  }

  .today-progress__stats {
    grid-template-columns: 1fr 1fr;
  }

  .today-progress__item {
    border-left: none;
    border-bottom: 1px solid #E5DDD2;
    padding: 10px 8px;
  }

  .today-progress__item:nth-child(n + 3) {
    border-bottom: none;
  }
}

@media (max-width: 575.98px) {
  .ielts-panel__mascot {
    width: 96px;
  }

  .service-card {
    flex: 1 1 100%;
    min-height: 220px;
    border-radius: 22px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .service-card,
  .service-card__icon,
  .service-card__arrow,
  .service-card__bar {
    transition: none;
  }
}
</style>
